import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Calendar } from '../../components/ui/calendar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../components/ui/popover';
import { useAuth } from '../../context/AuthContext';
import { consultationAPI } from '../../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { trackConsultationBooking, trackLead } from '../../lib/metaPixel';
import {
    Calendar as CalendarIcon,
    Users,
    FileText,
    MessageSquare,
    Clock,
    Check,
    ArrowRight,
    Video
} from 'lucide-react';

const ConsultationPage = () => {
    const { isAuthenticated, login, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [date, setDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [formData, setFormData] = useState({
        consultation_type: '',
        duration_minutes: 30,
        notes: '',
    });

    const consultationTypes = [
        {
            id: 'career_advice',
            name: 'Career Advice',
            description: 'General career guidance and planning',
            icon: Users,
        },
        {
            id: 'interview_prep',
            name: 'Interview Preparation',
            description: 'Mock interviews and feedback',
            icon: MessageSquare,
        },
        {
            id: 'cv_review',
            name: 'CV Review',
            description: 'Professional CV optimisation',
            icon: FileText,
        },
    ];

    // Fetch available slots when date changes
    const fetchAvailableSlots = async (selectedDate) => {
        if (!selectedDate) return;
        
        setLoadingSlots(true);
        setSelectedTime(null); // Reset time selection
        
        try {
            const response = await consultationAPI.getAvailableSlots(selectedDate.toISOString());
            setAvailableSlots(response.data.available_slots || []);
        } catch (error) {
            console.error('Error fetching slots:', error);
            // Fallback to all slots if endpoint fails
            setAvailableSlots([
                '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
                '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
            ]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleDateSelect = (newDate) => {
        setDate(newDate);
        if (newDate) {
            fetchAvailableSlots(newDate);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            login();
            return;
        }

        if (!date || !formData.consultation_type || !selectedTime) {
            toast.error('Please select a date, time and consultation type');
            return;
        }

        setLoading(true);
        try {
            const scheduledDate = new Date(date);
            const [hours, minutes] = selectedTime.split(':');
            scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            await consultationAPI.create({
                consultation_type: formData.consultation_type,
                scheduled_date: scheduledDate.toISOString(),
                duration_minutes: formData.duration_minutes,
                notes: formData.notes,
            });

            // Track consultation booking in Meta Pixel
            trackConsultationBooking(formData.consultation_type, scheduledDate.toISOString());
            trackLead('consultation', formData.consultation_type, 0);

            toast.success('Consultation booked successfully! We will contact you shortly.');
            navigate('/dashboard');
        } catch (error) {
            console.error('Booking error:', error);
            toast.error('Failed to book consultation');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-primary-950 via-primary-900 to-neutral-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Badge className="bg-white/10 text-white border-white/20 mb-6">
                        <Video className="w-3 h-3 mr-1" />
                        1-on-1 Sessions
                    </Badge>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                        Book a Consultation
                    </h1>
                    <p className="text-lg text-neutral-300 max-w-2xl mx-auto">
                        Get expert guidance on your job search journey. Our career specialists
                        are here to help you succeed.
                    </p>
                </div>
            </section>

            {/* Consultation Types */}
            <section className="py-12 bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold text-center mb-8">Choose Your Consultation Type</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {consultationTypes.map((type) => (
                            <div
                                key={type.id}
                                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                                    formData.consultation_type === type.id
                                        ? 'border-primary-600 bg-primary-50 shadow-lg'
                                        : 'border-neutral-200 hover:border-primary-300 hover:shadow-md'
                                }`}
                                onClick={() => setFormData({...formData, consultation_type: type.id})}
                                data-testid={`consultation-type-${type.id}`}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                                        formData.consultation_type === type.id
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-primary-100 text-primary-600'
                                    }`}>
                                        <type.icon className="w-8 h-8" />
                                    </div>
                                    <h3 className="font-semibold text-lg text-neutral-900 mb-1">{type.name}</h3>
                                    <p className="text-sm text-neutral-500 mb-3">{type.description}</p>
                                    {formData.consultation_type === type.id && (
                                        <Check className="w-6 h-6 text-primary-600 mt-3" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Booking Form */}
            <section className="py-16 bg-neutral-50">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Card>
                        <CardHeader>
                            <CardTitle>Schedule Your Session</CardTitle>
                            <CardDescription>
                                Select a date and time that works for you
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="text-sm font-medium text-neutral-700 mb-2 block">
                                        Select Date
                                    </label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className={`w-full justify-start text-left font-normal ${
                                                    !date && 'text-muted-foreground'
                                                }`}
                                                data-testid="date-picker-trigger"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {date ? format(date, 'PPP') : 'Pick a date'}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={date}
                                                onSelect={handleDateSelect}
                                                disabled={(date) => 
                                                    date < new Date() || 
                                                    date.getDay() === 0 || 
                                                    date.getDay() === 6
                                                }
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                {/* Time Slots */}
                                {date && (
                                    <div>
                                        <label className="text-sm font-medium text-neutral-700 mb-3 block">
                                            Select Time {loadingSlots && <span className="text-neutral-400">(Loading...)</span>}
                                        </label>
                                        {loadingSlots ? (
                                            <div className="text-center py-4 text-neutral-500">
                                                Checking availability...
                                            </div>
                                        ) : availableSlots.length > 0 ? (
                                            <div className="grid grid-cols-4 gap-2">
                                                {availableSlots.map((time) => (
                                                    <button
                                                        key={time}
                                                        type="button"
                                                        onClick={() => setSelectedTime(time)}
                                                        className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                                                            selectedTime === time
                                                                ? 'bg-primary-600 text-white border-primary-600'
                                                                : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'
                                                        }`}
                                                        data-testid={`time-slot-${time}`}
                                                    >
                                                        {time}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 text-amber-600 bg-amber-50 rounded-lg border border-amber-200">
                                                No slots available for this date. Please select another date.
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="text-sm font-medium text-neutral-700 mb-2 block">
                                        Duration
                                    </label>
                                    <Select
                                        value={String(formData.duration_minutes)}
                                        onValueChange={(value) => setFormData({...formData, duration_minutes: parseInt(value)})}
                                    >
                                        <SelectTrigger data-testid="duration-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="30">30 minutes</SelectItem>
                                            <SelectItem value="60">60 minutes</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-neutral-700 mb-2 block">
                                        Additional Notes (Optional)
                                    </label>
                                    <Textarea
                                        placeholder="Tell us what you'd like to discuss..."
                                        value={formData.notes}
                                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        rows={4}
                                        data-testid="notes-textarea"
                                    />
                                </div>

                                {/* Summary */}
                                {formData.consultation_type && date && selectedTime && (
                                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                                        <h4 className="font-semibold text-emerald-900 mb-2">Booking Summary</h4>
                                        <div className="space-y-1 text-sm text-emerald-800">
                                            <p><strong>Type:</strong> {consultationTypes.find(t => t.id === formData.consultation_type)?.name}</p>
                                            <p><strong>Date:</strong> {format(date, 'EEEE, MMMM d, yyyy')}</p>
                                            <p><strong>Time:</strong> {selectedTime}</p>
                                            <p><strong>Duration:</strong> {formData.duration_minutes} minutes</p>
                                        </div>
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-primary-600 hover:bg-primary-700"
                                    disabled={loading || !date || !formData.consultation_type || !selectedTime}
                                    data-testid="book-consultation-btn"
                                >
                                    {loading ? 'Booking...' : 'Book Consultation'}
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </section>
        </Layout>
    );
};

export default ConsultationPage;
